import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import type { Command } from "./CommandPalette";

export interface Menu {
  label: string;
  /** Command ids, or "-" for a separator. */
  items: string[];
}

interface MenuBarProps {
  menus: Menu[];
  commands: Command[];
}

/**
 * VS Code's menu bar (File, Edit, View…), built from the same commands as the command palette.
 * Click or Enter opens a menu; arrow keys move through items and across menus; Escape closes.
 */
export function MenuBar({ menus, commands }: MenuBarProps) {
  const [open, setOpen] = useState<number>();
  const barRef = useRef<HTMLDivElement>(null);
  const byId = new Map(commands.map((c) => [c.id, c]));

  // Clicking anywhere else closes the open menu.
  useEffect(() => {
    if (open === undefined) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!barRef.current?.contains(event.target as Node)) setOpen(undefined);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const focusItem = (menu: number, which: "first" | "last" | number) => {
    requestAnimationFrame(() => {
      const items = [
        ...(barRef.current?.querySelectorAll<HTMLElement>(
          `[data-menu="${menu}"] .menu [role="menuitem"]:not([aria-disabled="true"])`,
        ) ?? []),
      ];
      const index = which === "first" ? 0 : which === "last" ? items.length - 1 : which;
      items[(index + items.length) % items.length]?.focus();
    });
  };
  const openMenu = (index: number, focus: "first" | "last" = "first") => {
    const wrapped = (index + menus.length) % menus.length;
    setOpen(wrapped);
    focusItem(wrapped, focus);
  };
  const close = (refocus: number | undefined) => {
    setOpen(undefined);
    if (refocus !== undefined)
      barRef.current?.querySelectorAll<HTMLElement>(".menubar-button")[refocus]?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent, index: number) => {
    const items = [
      ...event.currentTarget.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      ),
    ];
    const current = items.indexOf(document.activeElement as HTMLElement);
    const keys: Record<string, () => void> = {
      ArrowDown: () => items[(current + 1) % items.length]?.focus(),
      ArrowUp: () => items[(current - 1 + items.length) % items.length]?.focus(),
      Home: () => items[0]?.focus(),
      End: () => items.at(-1)?.focus(),
      ArrowRight: () => openMenu(index + 1),
      ArrowLeft: () => openMenu(index - 1),
      Escape: () => close(index),
      Tab: () => close(undefined),
    };
    const action = keys[event.key];
    if (!action) return;
    if (event.key !== "Tab") event.preventDefault();
    action();
  };

  return (
    <div className="menubar" role="menubar" aria-label="Application menu" ref={barRef}>
      {menus.map((menu, index) => (
        <div className="menubar-entry" key={menu.label} data-menu={index}>
          <button
            type="button"
            role="menuitem"
            className="menubar-button"
            aria-haspopup="menu"
            aria-expanded={open === index}
            onClick={() => (open === index ? close(undefined) : openMenu(index))}
            // Like a native menu bar: once one menu is open, hovering another switches to it.
            onPointerEnter={() => open !== undefined && open !== index && setOpen(index)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                openMenu(index);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                openMenu(index, "last");
              } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                e.preventDefault();
                const buttons = barRef.current?.querySelectorAll<HTMLElement>(".menubar-button");
                const next =
                  (index + (e.key === "ArrowRight" ? 1 : -1) + menus.length) % menus.length;
                buttons?.[next]?.focus();
              }
            }}
          >
            {menu.label}
          </button>
          {open === index && (
            <div
              className="menu"
              role="menu"
              aria-label={menu.label}
              onKeyDown={(e) => onMenuKeyDown(e, index)}
            >
              {menu.items.map((id, i) => {
                if (id === "-")
                  // biome-ignore lint/suspicious/noArrayIndexKey: separators have no identity
                  return <hr key={`-${i}`} className="menu-separator" />;
                const command = byId.get(id);
                if (!command) return null;
                const disabled = Boolean(command.unavailable);
                return (
                  <button
                    key={id}
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    aria-disabled={disabled || undefined}
                    title={command.unavailable}
                    onClick={() => {
                      if (disabled) return;
                      close(undefined);
                      requestAnimationFrame(command.run);
                    }}
                  >
                    <span>{command.menuLabel ?? command.label}</span>
                    {command.shortcut && <kbd>{command.shortcut}</kbd>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
